import React, { useCallback, useState } from 'react';
import {
    Modal,
    ModalContent,
    ModalOverlay,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Button
} from '@chakra-ui/react';
import { deleteTemplateQuiz, QuizType, selectQuizLoading } from '@/redux/reducers/quizzes/quizSlice';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '@/redux/store';
import { QuizLibrary } from "@/features/Quiz";
import { AdminQuizV2ListItem, CustomerQuizItemDto, MyQuizV2ListItem } from '@/features/Quiz/types';
import { removeQuizLibrary } from "@/redux/reducers/quizzes/quizLibrary";
import { removeQuizAdmin } from "@/redux/reducers/quizzes/quizAdmin";
import { removeQuizAdminV2 } from '@/redux/reducers/quizzes/quizAdminV2';
import { removeMyQuizV2 } from '@/redux/reducers/quizzes/quizMyV2';
import { useDeleteQuiz } from "@/features/Quiz/api/deleteRegularQuiz";
import { removeQuizCustomer } from "@/redux/reducers/quizzes/quizCustomer";
import {useNotificationStore} from "@/stores/notifications";
import { deleteLibraryQuizV2 } from '@/features/Quiz/api/libraryQuizDetailV2';
import { useQueryClient } from 'react-query';

type LibraryQuizV2DeleteItem = {
    quizUuid: string;
    title: string;
};

type Props = {
    isOpen: boolean;
    onClose: () => void;
    quiz: QuizType | QuizLibrary | AdminQuizV2ListItem | CustomerQuizItemDto | LibraryQuizV2DeleteItem | MyQuizV2ListItem;
    quizType: 'regular' | 'admin' | 'template' | 'library' | 'libraryV2' | 'customer' | 'adminV2' | 'myV2';
};

export const DeleteQuizModal = ({ isOpen, onClose, quiz, quizType }: Props) => {
    const deleteQuizMutation = useDeleteQuiz(onClose);
    const dispatch = useDispatch<AppDispatch>();
    const isLoading = useSelector(selectQuizLoading, shallowEqual);
    const {addNotification} = useNotificationStore();
    const queryClient = useQueryClient();
    const [isDeletingV2, setIsDeletingV2] = useState(false);

    const handleDelete = useCallback(async () => {
        if (quizType === 'libraryV2') {
            const q = quiz as LibraryQuizV2DeleteItem;
            setIsDeletingV2(true);
            try {
                await deleteLibraryQuizV2(q.quizUuid);
                await queryClient.invalidateQueries(['library-quiz-v2']);
                onClose();
                addNotification({
                    type: 'success',
                    title: 'Quiz deleted successfully',
                    message: 'Changes will be reflected in a moment.',
                });
            } catch (err: unknown) {
                const ax = err as { response?: { data?: { message?: string } }; message?: string };
                const msg =
                    ax?.response?.data?.message ??
                    (typeof ax?.message === 'string' ? ax.message : null) ??
                    'Failed to delete quiz';
                addNotification({
                    type: 'error',
                    title: typeof msg === 'string' ? msg : 'Failed to delete quiz',
                });
            } finally {
                setIsDeletingV2(false);
            }
            return;
        }
        if (quizType === 'library') {
            await dispatch(removeQuizLibrary(quiz.id))
        }
        if (quizType === 'admin') {
            await dispatch(removeQuizAdmin(quiz.id))
        }
        if (quizType === 'adminV2') {
            const q = quiz as AdminQuizV2ListItem;
            await dispatch(removeQuizAdminV2({ quizUuid: q.quizUuid, id: q.id }));
        }
        if (quizType === 'myV2') {
            const q = quiz as MyQuizV2ListItem;
            await dispatch(removeMyQuizV2({ quizUuid: q.uuid, id: q.id }));
        }
        if (quizType === 'template') {
            await dispatch(deleteTemplateQuiz(quiz.id));
        }
        if (quizType === 'customer') {
            await dispatch(removeQuizCustomer(quiz.id));
        }
        if (quizType === 'regular') {
           await deleteQuizMutation.mutate(quiz.id);
        }
        addNotification({
            type: 'success',
            title: 'Quiz deleted successfully',
            message: 'Changes will be reflected in a moment.',
        });
    }, [dispatch, quiz, quizType, deleteQuizMutation, addNotification, queryClient, onClose]);

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Delete Quiz</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    Are you sure you want to delete Quiz: <b>{quiz.title}</b> ?
                </ModalBody>

                <ModalFooter>
                    <Button mr={3} onClick={onClose}>
                        Close
                    </Button>
                    <Button colorScheme="red" onClick={handleDelete} isLoading={isLoading || deleteQuizMutation.isLoading || isDeletingV2}>
                        Delete
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};
